import type { Facility, FacilityPricing } from '@/types';
import { CommissionManager } from './commission/CommissionManager';

/**
 * Indonesian Public Holidays 2026
 * Basic list for holiday detection
 */
const INDONESIAN_HOLIDAYS_2026 = [
    '2026-01-01', // New Year
    '2026-02-17', // Isra Miraj
    ' 2026-03-14', // Nyepi (Saka New Year)
    '2026-03-31', // Eid al-Fitr (estimated)
    '2026-04-01', // Eid al-Fitr Holiday
    '2026-04-02', // Eid al-Fitr Holiday
    '2026-04-03', // Good Friday
    '2026-05-01', // Labor Day
    '2026-05-07', // Ascension of Jesus Christ
    '2026-05-26', // Vesak Day
    '2026-06-01', // Pancasila Day
    '2026-06-07', // Eid al-Adha (estimated)
    '2026-06-28', // Islamic New Year
    '2026-08-17', // Independence Day
    '2026-09-06', // Prophet Muhammad's Birthday
    '2026-12-25', // Christmas
];

/**
 * Detect day type based on date
 * @param date - Date to check
 * @returns 'Weekday' | 'Weekend' | 'Holiday'
 */
export function getDayType(date: Date): 'Weekday' | 'Weekend' | 'Holiday' {
    // Check if it's a holiday
    const dateStr = date.toISOString().split('T')[0];
    if (INDONESIAN_HOLIDAYS_2026.includes(dateStr)) {
        return 'Holiday';
    }

    // Check if it's weekend (Saturday = 6, Sunday = 0)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return 'Weekend';
    }

    return 'Weekday';
}

/**
 * Get pricing for a specific date and time
 * @param facility - Facility object with pricing array
 * @param date - Date to check
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns Applicable pricing or null
 */
export function getPricingForDateTime(
    facility: Facility,
    date: Date,
    startTime: string,
    endTime: string
): FacilityPricing | null {
    if (!facility?.facility_pricing || !Array.isArray(facility.facility_pricing)) {
        return null;
    }

    const dayType = getDayType(date);

    // Find pricing that matches the day type and time range
    const applicablePricing = facility.facility_pricing.find((pricing) => {
        return pricing.day_type === dayType &&
            isTimeInRange(startTime, endTime, pricing.start_time, pricing.end_time);
    });

    return applicablePricing || null;
}

/**
 * Check if booking time is within pricing time range
 */
function isTimeInRange(
    bookingStart: string,
    bookingEnd: string,
    pricingStart: string,
    pricingEnd: string
): boolean {
    // Convert times to minutes for comparison
    const bookingStartMin = timeToMinutes(bookingStart);
    let bookingEndMin = timeToMinutes(bookingEnd);
    const pricingStartMin = timeToMinutes(pricingStart);
    let pricingEndMin = timeToMinutes(pricingEnd);

    // If pricing range spans across midnight or represents a full day (e.g. 12:00 to 11:59)
    if (pricingEndMin <= pricingStartMin) {
        pricingEndMin += 1440;
    }

    if (bookingEndMin <= bookingStartMin) {
        bookingEndMin += 1440;
    }

    return bookingStartMin >= pricingStartMin && bookingEndMin <= pricingEndMin;
}

/**
 * Convert time string (HH:mm:ss or HH:mm) to minutes
 */
function timeToMinutes(time: string): number {
    const parts = time.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return hours * 60 + minutes;
}

/**
 * Calculate total price based on pricing and duration
 * @param pricing - Facility pricing object
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns Total price
 */
export function calculateTotalPrice(
    pricing: FacilityPricing,
    startTime: string,
    endTime: string
): number {
    const duration = calculateDuration(startTime, endTime);
    const pricePerHour = parseFloat(pricing.price_per_hour);
    const basePrice = pricePerHour * duration;

    // Apply commission
    const commission = CommissionManager.calculate(basePrice, 'FACILITY_BOOKING');
    return commission.totalGuestPrice;
}

/**
 * Get pricing preview with commission breakdown
 */
export function getPricingPreview(
    pricing: FacilityPricing,
    startTime: string,
    endTime: string
) {
    const duration = calculateDuration(startTime, endTime);
    const pricePerHour = parseFloat(pricing.price_per_hour);
    const basePrice = pricePerHour * duration;

    const commission = CommissionManager.calculate(basePrice, 'FACILITY_BOOKING');

    return {
        base_price: basePrice,
        duration_hours: duration,
        total_price: commission.totalGuestPrice,
        guest_fee: commission.guestFee,
        provider_revenue: commission.providerRevenue,
        breakdown: {
            base: basePrice,
            hourly_rate: pricePerHour,
            additional_fees: 0,
            guest_fee: commission.guestFee,
        },
    };
}

/**
 * Calculate duration in hours
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @returns Duration in hours
 */
export function calculateDuration(startTime: string, endTime: string): number {
    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);
    return (endMin - startMin) / 60;
}

/**
 * Format price range from facility pricing array
 * @param pricings - Array of facility pricing
 * @returns Formatted price range string (e.g., "Rp 50.000 - Rp 250.000")
 */
export function formatPriceRange(pricings: FacilityPricing[] | undefined): string {
    if (!pricings || pricings.length === 0) return 'N/A';

    const prices = pricings.map((p) => parseFloat(p.price_per_hour));
    const min = Math.min(...prices);
    const max = Math.max(...prices);

    if (min === max) {
        return `Rp ${min.toLocaleString('id-ID')}`;
    }

    return `Rp ${min.toLocaleString('id-ID')} - Rp ${max.toLocaleString('id-ID')}`;
}

/**
 * Get price per hour as number
 */
export function getPricePerHour(pricing: FacilityPricing): number {
    return parseFloat(pricing.price_per_hour);
}

/**
 * Format time string to display format
 * @param time - Time in HH:mm:ss or HH:mm format
 * @returns Formatted time (e.g., "08:00")
 */
export function formatTime(time: string): string {
    const parts = time.split(':');
    return `${parts[0]}:${parts[1]}`;
}
